import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { Plus, Search, Filter, Eye, Edit, Clock, CheckCircle, XCircle, DollarSign, RefreshCw, User, Phone, Printer, FileText } from 'lucide-react';
import { RestaurantOrder } from '../types';
import { apiService } from '../services/api';
import CreateOrderModal from './CreateOrderModal';
import OrderDetailsModal from './OrderDetailsModal';
import jsPDF from 'jspdf';

export interface OrderManagementRef {
  openCreateModal: () => void;
  refreshData: () => void;
  setSearchTerm: (term: string) => void;
  setStatusFilter: (filter: string) => void;
}

const OrderManagement = forwardRef<OrderManagementRef>((props, ref) => {
  const [orders, setOrders] = useState<RestaurantOrder[]>([]);
  const [filteredOrders, setFilteredOrders] = useState<RestaurantOrder[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<RestaurantOrder | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const statusOptions = [
    { value: 'all', label: 'All Orders' },
    { value: 'pending', label: 'Pending' },
    { value: 'confirmed', label: 'Confirmed' },
    { value: 'preparing', label: 'Preparing' },
    { value: 'ready', label: 'Ready' },
    { value: 'delivered', label: 'Delivered' },
    { value: 'cancelled', label: 'Cancelled' },
  ];

  const statusColors = {
    pending: 'bg-yellow-100 text-yellow-800',
    confirmed: 'bg-blue-100 text-blue-800',
    preparing: 'bg-orange-100 text-orange-800',
    ready: 'bg-green-100 text-green-800',
    delivered: 'bg-green-100 text-green-800',
    cancelled: 'bg-red-100 text-red-800',
  };

  const paymentStatusColors = {
    pending: 'bg-yellow-100 text-yellow-800',
    paid: 'bg-green-100 text-green-800',
    refunded: 'bg-red-100 text-red-800',
  };

  useEffect(() => {
    loadOrders();
  }, []);

  useEffect(() => {
    filterOrders();
  }, [orders, searchTerm, statusFilter]);

  // Expose functions to parent component
  useImperativeHandle(ref, () => ({
    openCreateModal: () => setShowCreateModal(true),
    refreshData: () => loadOrders(true),
    setSearchTerm: (term: string) => setSearchTerm(term),
    setStatusFilter: (filter: string) => setStatusFilter(filter),
  }));

  const loadOrders = async (isRefresh = false) => {
    try {
      if (isRefresh) {
        setIsRefreshing(true);
      } else {
        setIsLoading(true);
      }
      setError(null);
      const data = await apiService.getRestaurantOrders();
      
      // Debug: Log the API response
      console.log('API Response:', data);
      
      // Transform API response to match frontend interface
      const transformedOrders = data.map((order: any) => ({
        id: order.restaurant_order_id || 0,
        customerName: order.customer_name || 'Unknown Customer',
        customerPhone: order.customer_phone || '',
        status: order.order_status || 'pending',
        paymentStatus: order.payment_status || 'pending',
        totalAmount: parseFloat(order.total_amount || order.calculated_total || 0),
        orderTime: order.order_time || new Date().toISOString(),
        deliveryTime: order.served_time || null,
        notes: order.special_instructions || '',
        tableNumber: order.table_number || '',
        items: order.items ? order.items.map((item: any) => ({
          id: item.order_item_id,
          name: item.item_name,
          quantity: item.quantity,
          unitPrice: parseFloat(item.unit_price),
          totalPrice: parseFloat(item.total_price),
          notes: item.special_instructions || undefined
        })) : []
      }));
      
      // Debug: Log the transformed orders
      console.log('Transformed Orders:', transformedOrders);
      
      setOrders(transformedOrders);
    } catch (err) {
      setError('Failed to load orders');
      console.error('Error loading orders:', err);
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  };

  const handleRefresh = () => {
    loadOrders(true);
  };

  const filterOrders = () => {
    let filtered = orders;

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(order =>
        order.customerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        order.customerPhone?.includes(searchTerm) ||
        order.id.toString().includes(searchTerm)
      );
    }

    // Filter by status
    if (statusFilter !== 'all') {
      filtered = filtered.filter(order => order.status === statusFilter);
    }

    setFilteredOrders(filtered);
  };

  const handleCreateOrder = async (orderData: any) => {
    try {
      const response = await apiService.createRestaurantOrder(orderData);
      
      // Transform the API response to match our frontend interface
      const transformedOrder: RestaurantOrder = {
        id: (response as any).order.restaurant_order_id,
        customerName: (response as any).order.customer_name,
        customerPhone: (response as any).order.customer_phone || '',
        status: (response as any).order.order_status,
        paymentStatus: (response as any).order.payment_status,
        totalAmount: parseFloat((response as any).order.total_amount),
        orderTime: (response as any).order.order_time,
        deliveryTime: (response as any).order.served_time || null,
        notes: (response as any).order.special_instructions || '',
        tableNumber: (response as any).order.table_number,
        items: (response as any).order.items.map((item: any) => ({
          id: item.order_item_id,
          name: item.item_name,
          quantity: item.quantity,
          unitPrice: parseFloat(item.unit_price),
          totalPrice: parseFloat(item.total_price),
          notes: item.special_instructions || undefined
        }))
      };
      
      setOrders(prev => [transformedOrder, ...prev]);
      setShowCreateModal(false);
    } catch (err) {
      console.error('Error creating order:', err);
      throw err;
    }
  };

  const handleUpdateOrder = async (orderId: number, updates: any) => {
    try {
      // This would be called from the OrderDetailsModal
      await loadOrders(); // Reload orders to get updated data
    } catch (err) {
      console.error('Error updating order:', err);
    }
  };

  const handleViewOrder = (order: RestaurantOrder) => {
    setSelectedOrder(order);
    setShowDetailsModal(true);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
    }).format(amount);
  };

  const formatDateTime = (dateTime: string) => {
    return new Date(dateTime).toLocaleString('en-IN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const generateKOT = (order: RestaurantOrder) => {
    const doc = new jsPDF();
    
    // Header
    doc.setFontSize(20);
    doc.setFont('helvetica', 'bold');
    doc.text('KITCHEN ORDER TICKET', 105, 20, { align: 'center' });
    
    // Restaurant info
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    doc.text('Hotel Continental', 105, 30, { align: 'center' });
    doc.text('Powered by Snowtable', 105, 35, { align: 'center' });
    
    // Order details
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text(`Order #${order.id}`, 20, 50);
    doc.text(`Table: ${order.tableNumber || 'N/A'}`, 20, 60);
    doc.text(`Time: ${formatDateTime(order.orderTime)}`, 20, 70);
    
    // Customer info
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    doc.text(`Customer: ${order.customerName}`, 20, 85);
    if (order.customerPhone) {
      doc.text(`Phone: ${order.customerPhone}`, 20, 95);
    }
    
    // Items
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('ITEMS TO PREPARE:', 20, 110);
    
    let yPosition = 125;
    order.items.forEach((item, index) => {
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text(`${index + 1}. ${item.name}`, 25, yPosition);
      
      doc.setFont('helvetica', 'normal');
      doc.text(`Qty: ${item.quantity}`, 25, yPosition + 8);
      
      if (item.notes) {
        doc.setFontSize(10);
        doc.text(`Note: ${item.notes}`, 25, yPosition + 16);
        yPosition += 25;
      } else {
        yPosition += 20;
      }
    });
    
    // Special instructions
    if (order.notes) {
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text('Special Instructions:', 20, yPosition + 10);
      doc.setFont('helvetica', 'normal');
      doc.text(order.notes, 20, yPosition + 20);
    }
    
    // Footer
    doc.setFontSize(10);
    doc.setFont('helvetica', 'italic');
    doc.text('Generated at: ' + new Date().toLocaleString('en-IN'), 105, doc.internal.pageSize.height - 20, { align: 'center' });
    
    // Open PDF in new tab instead of downloading
    const pdfBlob = doc.output('blob');
    const pdfUrl = URL.createObjectURL(pdfBlob);
    window.open(pdfUrl, '_blank');
  };

  const generateInvoice = (order: RestaurantOrder) => {
    const doc = new jsPDF();
    
    // Header
    doc.setFontSize(20);
    doc.setFont('helvetica', 'bold');
    doc.text('INVOICE', 105, 20, { align: 'center' });
    
    // Restaurant info
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    doc.text('Hotel Continental', 105, 30, { align: 'center' });
    doc.text('Powered by Snowtable', 105, 35, { align: 'center' });
    
    // Invoice details
    doc.setFontSize(12);
    doc.text(`Invoice #: ${order.id}`, 20, 50);
    doc.text(`Date: ${formatDateTime(order.orderTime)}`, 20, 60);
    doc.text(`Table: ${order.tableNumber || 'N/A'}`, 20, 70);
    
    // Customer info
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Bill To:', 20, 90);
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    doc.text(order.customerName, 20, 100);
    if (order.customerPhone) {
      doc.text(order.customerPhone, 20, 110);
    }
    
    // Items table
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Items Ordered:', 20, 130);
    
    // Table header
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text('Item', 20, 145);
    doc.text('Qty', 120, 145);
    doc.text('Price', 140, 145);
    doc.text('Total', 170, 145);
    
    // Table line
    doc.line(20, 150, 190, 150);
    
    let yPosition = 160;
    let subtotal = 0;
    
    order.items.forEach((item) => {
      const itemTotal = item.quantity * item.unitPrice;
      subtotal += itemTotal;
      
      doc.setFont('helvetica', 'normal');
      doc.text(item.name, 20, yPosition);
      doc.text(item.quantity.toString(), 120, yPosition);
      doc.text(formatCurrency(item.unitPrice), 140, yPosition);
      doc.text(formatCurrency(itemTotal), 170, yPosition);
      
      if (item.notes) {
        doc.setFontSize(8);
        doc.text(`Note: ${item.notes}`, 25, yPosition + 5);
        yPosition += 15;
      } else {
        yPosition += 10;
      }
    });
    
    // Total
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Total Amount:', 140, yPosition + 10);
    doc.text(formatCurrency(order.totalAmount), 170, yPosition + 10);
    
    // Payment status
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(`Payment Status: ${order.paymentStatus.toUpperCase()}`, 20, yPosition + 25);
    doc.text(`Order Status: ${order.status.toUpperCase()}`, 20, yPosition + 35);
    
    // Footer
    doc.setFontSize(10);
    doc.setFont('helvetica', 'italic');
    doc.text('Thank you for dining with us!', 105, doc.internal.pageSize.height - 30, { align: 'center' });
    doc.text('Generated at: ' + new Date().toLocaleString('en-IN'), 105, doc.internal.pageSize.height - 20, { align: 'center' });
    
    // Open PDF in new tab instead of downloading
    const pdfBlob = doc.output('blob');
    const pdfUrl = URL.createObjectURL(pdfBlob);
    window.open(pdfUrl, '_blank');
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="glass-card p-8 rounded-2xl shadow-card text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading orders...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">


      {/* Error State */}
      {error && (
        <div className="glass-card p-6 rounded-2xl shadow-card border-l-4 border-red-500">
          <div className="flex items-center gap-3">
            <XCircle className="w-6 h-6 text-red-500" />
            <div>
              <h3 className="font-semibold text-red-800">Error</h3>
              <p className="text-red-600">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Orders List - Card Layout */}
      <div className="glass-card rounded-2xl shadow-card p-6">
        {filteredOrders.length === 0 ? (
          <div className="p-12 text-center">
            <Clock className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-600 mb-2">No orders found</h3>
            <p className="text-gray-500">
              {searchTerm || statusFilter !== 'all' 
                ? 'Try adjusting your search or filter criteria'
                : 'Create your first order to get started'
              }
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredOrders.map((order) => (
              <div 
                key={order.id} 
                onClick={() => handleViewOrder(order)}
                className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-lg hover:border-primary-300 transition-all duration-200 cursor-pointer group flex flex-col h-full"
              >
                {/* Card Content - Flexible */}
                <div className="flex-1 flex flex-col">
                  {/* Order Header */}
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <span className="text-lg font-bold text-primary-500">#{order.id}</span>
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${statusColors[order.status] || 'bg-gray-100 text-gray-800'}`}>
                        {order.status ? order.status.charAt(0).toUpperCase() + order.status.slice(1) : 'Unknown'}
                      </span>
                    </div>
                    <div className="text-right">
                      <div className="text-lg font-bold text-gray-900">{formatCurrency(order.totalAmount)}</div>
                      <div className="text-xs text-gray-500">{order.items.length} items</div>
                    </div>
                  </div>

                  {/* Customer Info */}
                  <div className="mb-4">
                    <div className="flex items-center gap-2 mb-2">
                      <User className="w-4 h-4 text-gray-500" />
                      <span className="font-medium text-gray-900">{order.customerName}</span>
                    </div>
                    {order.customerPhone && (
                      <div className="flex items-center gap-2 mb-2">
                        <Phone className="w-4 h-4 text-gray-500" />
                        <span className="text-sm text-gray-600">{order.customerPhone}</span>
                      </div>
                    )}
                    {order.tableNumber && (
                      <div className="flex items-center gap-2">
                        <span className="w-4 h-4 bg-primary-100 rounded-full flex items-center justify-center">
                          <span className="text-xs font-bold text-primary-600">T</span>
                        </span>
                        <span className="text-sm text-gray-600">Table {order.tableNumber}</span>
                      </div>
                    )}
                  </div>

                  {/* Payment Status */}
                  <div className="mb-4">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${paymentStatusColors[order.paymentStatus] || 'bg-gray-100 text-gray-800'}`}>
                      Payment: {order.paymentStatus ? order.paymentStatus.charAt(0).toUpperCase() + order.paymentStatus.slice(1) : 'Unknown'}
                    </span>
                  </div>

                  {/* Order Time */}
                  <div className="flex items-center gap-2 text-sm text-gray-500 mb-4">
                    <Clock className="w-4 h-4" />
                    <span>{formatDateTime(order.orderTime)}</span>
                  </div>

                  {/* Order Items Preview - Flexible */}
                  <div className="flex-1 border-t border-gray-100 pt-4">
                    <div className="text-xs text-gray-500 mb-2">Order Items:</div>
                    <div className="space-y-1">
                      {order.items.slice(0, 2).map((item, index) => (
                        <div key={index} className="flex justify-between text-sm">
                          <span className="text-gray-700 truncate">{item.name}</span>
                          <span className="text-gray-500">x{item.quantity}</span>
                        </div>
                      ))}
                      {order.items.length > 2 && (
                        <div className="text-xs text-gray-500 italic">
                          +{order.items.length - 2} more items
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Print Buttons - Fixed at bottom */}
                <div className="mt-4 pt-4 border-t border-gray-100 flex gap-2">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      generateKOT(order);
                    }}
                    className="flex-1 flex items-center justify-center gap-2 bg-orange-200 text-orange-800 px-3 py-2 rounded-lg hover:bg-orange-300 transition-colors text-sm font-medium"
                  >
                    <Printer className="w-4 h-4" />
                    KOT
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      generateInvoice(order);
                    }}
                    className="flex-1 flex items-center justify-center gap-2 bg-green-200 text-green-800 px-3 py-2 rounded-lg hover:bg-green-300 transition-colors text-sm font-medium"
                  >
                    <FileText className="w-4 h-4" />
                    Invoice
                  </button>
                </div>

              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modals */}
      {showCreateModal && (
        <CreateOrderModal
          onClose={() => setShowCreateModal(false)}
          onSubmit={handleCreateOrder}
        />
      )}

      {showDetailsModal && selectedOrder && (
        <OrderDetailsModal
          order={selectedOrder}
          onClose={() => {
            setShowDetailsModal(false);
            setSelectedOrder(null);
          }}
          onUpdate={handleUpdateOrder}
        />
      )}
    </div>
  );
});

export default OrderManagement;
